/**
 * Auto Routing Engine
 * 
 * A* pathfinding for MEP routing with obstacle avoidance.
 * Supports duct, pipe, cable tray, and conduit routing.
 */

import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';

// ==================== Types ====================

export type RoutingSystemType = 'Duct' | 'Pipe' | 'CableTray' | 'Conduit';

export interface RoutingNode {
    id: string;
    position: THREE.Vector3;
    gCost: number;  // Cost from start
    hCost: number;  // Heuristic to end
    fCost: number;  // g + h
    parent?: RoutingNode;
    direction?: THREE.Vector3;
}

export interface RoutingObstacle {
    id: string;
    type: 'Box' | 'Cylinder' | 'Mesh';
    bounds: THREE.Box3;
    clearance: number;  // Required clearance in meters
}

export interface RoutingPreferences {
    preferHorizontal: boolean;
    preferVertical: boolean;
    minimizeBends: boolean;
    minimizeLength: boolean;
    maxBends: number;
    minSegmentLength: number;  // meters
    gridResolution: number;    // meters
    elevationPreference: number;  // Preferred Z level
}

export interface RouteSegment {
    start: THREE.Vector3;
    end: THREE.Vector3;
    direction: THREE.Vector3;
    length: number;
    fittingType?: 'Elbow' | 'Tee' | 'Coupling' | 'Transition';
    fittingAngle?: number;
}

export interface RoutingResult {
    success: boolean;
    path: RouteSegment[];
    totalLength: number;
    bendCount: number;
    estimatedCost: number;
    errorMessage?: string;
}

// ==================== Priority Queue ====================

class PriorityQueue<T> {
    private items: { element: T; priority: number }[] = [];

    enqueue(element: T, priority: number): void {
        const item = { element, priority };
        let added = false;
        for (let i = 0; i < this.items.length; i++) {
            if (this.items[i].priority > priority) {
                this.items.splice(i, 0, item);
                added = true;
                break;
            }
        }
        if (!added) this.items.push(item);
    }

    dequeue(): T | undefined {
        return this.items.shift()?.element;
    }

    isEmpty(): boolean {
        return this.items.length === 0;
    }
}

// ==================== 3D Grid ====================

class SpatialGrid {
    private resolution: number;
    private obstacles: Map<string, RoutingObstacle> = new Map();
    private occupiedCells: Set<string> = new Set();

    constructor(resolution: number = 0.5) {
        this.resolution = resolution;
    }

    private getCellKey(x: number, y: number, z: number): string {
        const ix = Math.floor(x / this.resolution);
        const iy = Math.floor(y / this.resolution);
        const iz = Math.floor(z / this.resolution);
        return `${ix},${iy},${iz}`;
    }

    addObstacle(obstacle: RoutingObstacle): void {
        this.obstacles.set(obstacle.id, obstacle);

        // Mark cells as occupied
        const min = obstacle.bounds.min;
        const max = obstacle.bounds.max;
        const clearance = obstacle.clearance;

        for (let x = min.x - clearance; x <= max.x + clearance; x += this.resolution) {
            for (let y = min.y - clearance; y <= max.y + clearance; y += this.resolution) {
                for (let z = min.z - clearance; z <= max.z + clearance; z += this.resolution) {
                    this.occupiedCells.add(this.getCellKey(x, y, z));
                }
            }
        }
    }

    removeObstacle(id: string): void {
        this.obstacles.delete(id);
        // Rebuild occupied cells (simplified)
        this.occupiedCells.clear();
        for (const obs of this.obstacles.values()) {
            this.addObstacle(obs);
        }
    }

    isOccupied(position: THREE.Vector3): boolean {
        return this.occupiedCells.has(this.getCellKey(position.x, position.y, position.z));
    }

    isPathClear(start: THREE.Vector3, end: THREE.Vector3, radius: number = 0.1): boolean {
        const direction = end.clone().sub(start);
        const length = direction.length();
        direction.normalize();

        const steps = Math.ceil(length / this.resolution);
        for (let i = 0; i <= steps; i++) {
            const point = start.clone().add(direction.clone().multiplyScalar(i * this.resolution));

            // Check cell and neighbors
            for (let dx = -radius; dx <= radius; dx += this.resolution) {
                for (let dy = -radius; dy <= radius; dy += this.resolution) {
                    for (let dz = -radius; dz <= radius; dz += this.resolution) {
                        const checkPoint = point.clone().add(new THREE.Vector3(dx, dy, dz));
                        if (this.isOccupied(checkPoint)) return false;
                    }
                }
            }
        }
        return true;
    }

    getNeighbors(position: THREE.Vector3): THREE.Vector3[] {
        const neighbors: THREE.Vector3[] = [];
        const offsets = [
            [1, 0, 0], [-1, 0, 0],  // X
            [0, 1, 0], [0, -1, 0],  // Y
            [0, 0, 1], [0, 0, -1],  // Z
        ];

        for (const [dx, dy, dz] of offsets) {
            const neighbor = position.clone().add(
                new THREE.Vector3(dx * this.resolution, dy * this.resolution, dz * this.resolution)
            );
            if (!this.isOccupied(neighbor)) {
                neighbors.push(neighbor);
            }
        }

        return neighbors;
    }
}

// ==================== Auto Routing Engine ====================

export class AutoRoutingEngine {
    private grid: SpatialGrid;
    private preferences: RoutingPreferences;

    constructor(preferences?: Partial<RoutingPreferences>) {
        this.preferences = {
            preferHorizontal: true,
            preferVertical: false,
            minimizeBends: true,
            minimizeLength: false,
            maxBends: 8,
            minSegmentLength: 0.3,
            gridResolution: 0.25,
            elevationPreference: 3.0,
            ...preferences
        };
        this.grid = new SpatialGrid(this.preferences.gridResolution);
    }

    // ==================== Obstacle Management ====================

    public addObstacle(id: string, bounds: THREE.Box3, clearance: number = 0.15): void {
        this.grid.addObstacle({
            id,
            type: 'Box',
            bounds,
            clearance
        });
    }

    public addObstacleFromElement(element: any): void {
        if (!element.geometryData) return;

        // Calculate bounding box from element geometry
        let bounds: THREE.Box3;

        if (element.geometryData.start && element.geometryData.end) {
            // Linear element (beam, duct, pipe)
            const start = new THREE.Vector3(...element.geometryData.start);
            const end = new THREE.Vector3(...element.geometryData.end);
            bounds = new THREE.Box3().setFromPoints([start, end]);
            bounds.expandByScalar(0.2);  // Account for width
        } else if (element.geometryData.boundary) {
            // Surface element (floor, wall)
            const points = element.geometryData.boundary.map((p: number[]) =>
                new THREE.Vector3(p[0], p[1], 0)
            );
            bounds = new THREE.Box3().setFromPoints(points);
            bounds.expandByScalar(element.geometryData.thickness || 0.2);
        } else {
            return;
        }

        this.addObstacle(element.id, bounds);
    }

    public clearObstacles(): void {
        this.grid = new SpatialGrid(this.preferences.gridResolution);
    }

    // ==================== A* Pathfinding ====================

    public findRoute(
        start: THREE.Vector3,
        end: THREE.Vector3,
        systemType: RoutingSystemType = 'Duct'
    ): RoutingResult {
        // Validate inputs
        if (this.grid.isOccupied(start)) {
            return {
                success: false, path: [], totalLength: 0, bendCount: 0, estimatedCost: 0,
                errorMessage: 'Start position is blocked'
            };
        }
        if (this.grid.isOccupied(end)) {
            return {
                success: false, path: [], totalLength: 0, bendCount: 0, estimatedCost: 0,
                errorMessage: 'End position is blocked'
            };
        }

        const openSet = new PriorityQueue<RoutingNode>();
        const closedSet = new Set<string>();
        const nodeMap = new Map<string, RoutingNode>();

        const getKey = (pos: THREE.Vector3) =>
            `${Math.round(pos.x * 100)},${Math.round(pos.y * 100)},${Math.round(pos.z * 100)}`;

        const startNode: RoutingNode = {
            id: uuidv4(),
            position: start.clone(),
            gCost: 0,
            hCost: this.heuristic(start, end),
            fCost: this.heuristic(start, end)
        };

        openSet.enqueue(startNode, startNode.fCost);
        nodeMap.set(getKey(start), startNode);

        let iterations = 0;
        const maxIterations = 50000;

        while (!openSet.isEmpty() && iterations < maxIterations) {
            iterations++;
            const current = openSet.dequeue()!;
            const currentKey = getKey(current.position);

            // Check if reached goal
            if (current.position.distanceTo(end) < this.preferences.gridResolution) {
                return this.reconstructPath(current, systemType);
            }

            closedSet.add(currentKey);

            // Get neighbors
            const neighbors = this.grid.getNeighbors(current.position);

            for (const neighborPos of neighbors) {
                const neighborKey = getKey(neighborPos);
                if (closedSet.has(neighborKey)) continue;

                // Calculate costs
                const moveCost = this.calculateMoveCost(current, neighborPos);
                const newGCost = current.gCost + moveCost;

                let neighborNode = nodeMap.get(neighborKey);

                if (!neighborNode) {
                    neighborNode = {
                        id: uuidv4(),
                        position: neighborPos,
                        gCost: Infinity,
                        hCost: this.heuristic(neighborPos, end),
                        fCost: Infinity
                    };
                    nodeMap.set(neighborKey, neighborNode);
                }

                if (newGCost < neighborNode.gCost) {
                    neighborNode.gCost = newGCost;
                    neighborNode.fCost = newGCost + neighborNode.hCost;
                    neighborNode.parent = current;
                    neighborNode.direction = neighborPos.clone().sub(current.position).normalize();
                    openSet.enqueue(neighborNode, neighborNode.fCost);
                }
            }
        }

        return {
            success: false,
            path: [],
            totalLength: 0,
            bendCount: 0,
            estimatedCost: 0,
            errorMessage: iterations >= maxIterations ? 'Max iterations reached' : 'No path found'
        };
    }

    private heuristic(a: THREE.Vector3, b: THREE.Vector3): number {
        // Manhattan distance with preference weights
        let cost = Math.abs(a.x - b.x) + Math.abs(a.y - b.y) + Math.abs(a.z - b.z);

        // Penalize vertical movement if preferHorizontal
        if (this.preferences.preferHorizontal) {
            cost += Math.abs(a.z - b.z) * 0.5;
        }

        return cost;
    }

    private calculateMoveCost(from: RoutingNode, to: THREE.Vector3): number {
        let cost = from.position.distanceTo(to);

        // Bend penalty
        if (from.direction) {
            const newDirection = to.clone().sub(from.position).normalize();
            const dot = from.direction.dot(newDirection);
            if (dot < 0.99) {
                cost += this.preferences.minimizeBends ? 2.0 : 0.5;
            }
        }

        // Elevation penalty
        if (this.preferences.preferHorizontal) {
            const zDiff = Math.abs(to.z - from.position.z);
            cost += zDiff * 1.5;
        }

        return cost;
    }

    private reconstructPath(endNode: RoutingNode, systemType: RoutingSystemType): RoutingResult {
        const rawPoints: THREE.Vector3[] = [];
        let current: RoutingNode | undefined = endNode;

        while (current) {
            rawPoints.unshift(current.position.clone());
            current = current.parent;
        }

        // Simplify path - merge collinear segments
        const simplifiedPoints = this.simplifyPath(rawPoints);

        // Convert to segments
        const segments: RouteSegment[] = [];
        let totalLength = 0;
        let bendCount = 0;
        let prevDirection: THREE.Vector3 | null = null;

        for (let i = 0; i < simplifiedPoints.length - 1; i++) {
            const start = simplifiedPoints[i];
            const end = simplifiedPoints[i + 1];
            const direction = end.clone().sub(start).normalize();
            const length = start.distanceTo(end);

            // Check for bend
            let fittingType: RouteSegment['fittingType'];
            let fittingAngle: number | undefined;

            if (prevDirection) {
                const dot = prevDirection.dot(direction);
                if (dot < 0.99) {
                    fittingType = 'Elbow';
                    fittingAngle = Math.acos(Math.min(1, Math.max(-1, dot))) * 180 / Math.PI;
                    bendCount++;
                }
            }

            segments.push({
                start: start.clone(),
                end: end.clone(),
                direction,
                length,
                fittingType,
                fittingAngle
            });

            totalLength += length;
            prevDirection = direction;
        }

        // Estimate cost based on system type
        const costPerMeter = systemType === 'Duct' ? 50 : systemType === 'Pipe' ? 80 : 30;
        const fittingCost = systemType === 'Duct' ? 25 : systemType === 'Pipe' ? 40 : 15;
        const estimatedCost = totalLength * costPerMeter + bendCount * fittingCost;

        return {
            success: true,
            path: segments,
            totalLength,
            bendCount,
            estimatedCost
        };
    }

    private simplifyPath(points: THREE.Vector3[]): THREE.Vector3[] {
        if (points.length <= 2) return points;

        const simplified: THREE.Vector3[] = [points[0]];
        let lastDirection = points[1].clone().sub(points[0]).normalize();

        for (let i = 2; i < points.length; i++) {
            const currentDirection = points[i].clone().sub(points[i - 1]).normalize();

            // If direction changed significantly, add the previous point
            if (lastDirection.dot(currentDirection) < 0.99) {
                simplified.push(points[i - 1]);
                lastDirection = currentDirection;
            }
        }

        simplified.push(points[points.length - 1]);
        return simplified;
    }

    // ==================== High-Level Methods ====================

    public routeToConnector(
        startElement: any,
        endElement: any,
        systemType: RoutingSystemType = 'Duct'
    ): RoutingResult {
        // Extract connector positions from elements
        const startPos = this.getConnectorPosition(startElement, 'out');
        const endPos = this.getConnectorPosition(endElement, 'in');

        if (!startPos || !endPos) {
            return {
                success: false, path: [], totalLength: 0, bendCount: 0, estimatedCost: 0,
                errorMessage: 'Could not find connectors on elements'
            };
        }

        return this.findRoute(startPos, endPos, systemType);
    }

    private getConnectorPosition(element: any, type: 'in' | 'out'): THREE.Vector3 | null {
        if (element.geometryData?.connectors) {
            const connector = element.geometryData.connectors.find((c: any) =>
                (type === 'out' && c.flow === 'Out') || (type === 'in' && c.flow === 'In')
            );
            if (connector) {
                return new THREE.Vector3(...connector.position);
            }
        }

        // Fallback to element center/endpoints
        if (element.geometryData?.end) {
            return type === 'out'
                ? new THREE.Vector3(...element.geometryData.end)
                : new THREE.Vector3(...element.geometryData.start);
        }

        return null;
    }

    public setPreferences(prefs: Partial<RoutingPreferences>): void {
        this.preferences = { ...this.preferences, ...prefs };
    }
}
